import clsx from 'clsx';
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';

// Components
import SelectWithSPIcon from '@/components/base/Select/SelectWithSPIcon';
import SelectFilter from '@/components/base/SelectFilter';
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';

// Hooks
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';

// Lib
import { INSTAGRAM } from '@/lib/socialProfile';

const SpOption = ({ option, selected, active }) => {
    return (
        <div className="flex items-center" data-testid={`test-${option?.slug}`}>
            {option?.value === 'all' ? (
                <Icon
                    className={clsx('mr-px px-2 text-base', { 'text-icoBlue': active || selected })}
                    icon="single-profile"
                />
            ) : (
                <Avatar
                    fallback={option?.slug?.[0]}
                    size="sm"
                    profileType={option?.social_network_type}
                    url={option?.external_api_data?.avatar}
                />
            )}
            <div className="flex flex-col overflow-hidden pl-3.5 text-left">
                <span
                    className={clsx('truncate font-GSemiBold text-sm transition-all', {
                        'text-icoBlue': selected || active,
                        'text-icoBlack dark:text-white': !selected && !active
                    })}>
                    {option?.slug ?? option?.label}
                </span>
                {option?.value !== 'all' && (
                    <span
                        className={clsx('truncate font-GMedium text-xs transition-all', {
                            'text-icoBlue': selected || active,
                            'text-icoGray-500 dark:text-icoDarkMode-wolf': !active && !selected
                        })}>
                        {option?.displayName ?? option?.label}
                    </span>
                )}
            </div>
        </div>
    );
};

SpOption.propTypes = {
    option: PropTypes.object,
    selected: PropTypes.bool,
    active: PropTypes.bool
};

const SelectSpWidgetDashboardView = ({
    selectedSP,
    setSelectedSP,
    withAllLabel = false,
    networks = null,
    noValue = false,
    baseStyle = 'noBorder',
    avatarSize = 'sm',
    sizeStyle = 'default',
    buttonClassName
}) => {
    const { t } = useTranslation('dashboard');
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const spOptions = useMemo(() => {
        const socialProfiles = networks
            ? activatedSocialProfiles.filter((social_profile) =>
                  networks.includes(social_profile.social_network_type)
              )
            : activatedSocialProfiles;

        const options = socialProfiles.map((profile) => {
            return {
                label: profile.slug,
                displayName: profile?.external_api_data?.name ?? profile?.slug,
                value: profile.id,
                ...profile
            };
        });
        if (withAllLabel) {
            const allOption = {
                label: t('dashboard_select_all_social_profiles'),
                value: 'all',
                slug: t('dashboard_select_all_social_profiles')
            };
            return [allOption, ...options];
        }
        return options;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [networks]);

    return (
        <SelectFilter
            selected={
                spOptions?.filter((profile) => profile.id === selectedSP?.id)[0] || selectedSP
            }
            setSelected={setSelectedSP}
            noBorder
            noValue={noValue}
            className="min-w-[3.2rem]"
            idInput="select-filter-dashboard"
            options={spOptions}
            button={
                <SelectWithSPIcon
                    sizeStyle={sizeStyle}
                    avatarSize={avatarSize}
                    baseStyle={baseStyle}
                    profileData={selectedSP}
                    noValue={noValue}
                    withDisplayName
                    socialNetworkType={selectedSP?.social_network_type ?? INSTAGRAM}
                    className={buttonClassName}
                />
            }>
            <SpOption />
        </SelectFilter>
    );
};

SelectSpWidgetDashboardView.propTypes = {
    selectedSP: PropTypes.object,
    setSelectedSP: PropTypes.func,
    withAllLabel: PropTypes.bool,
    networks: PropTypes.arrayOf(PropTypes.string),
    noValue: PropTypes.bool,
    baseStyle: PropTypes.string,
    avatarSize: PropTypes.string,
    sizeStyle: PropTypes.string
};

export default SelectSpWidgetDashboardView;
